import * as THREE from "three";
import { Scene } from "../../engine/core/Scene";
import { CharacterModel } from "../../engine/render/CharacterModel";
import { MonsterModel } from "../../engine/render/MonsterModel";
import { FieldScene } from "../../engine/scenes/FieldScene";
import type { World } from "../../engine/rpg/World";

/** Title card: the hero and a slime, circling on a plinth. */
export class TitleScene extends Scene {
  private hero!: CharacterModel;
  private slime!: MonsterModel;
  private overlay!: HTMLDivElement;
  private angle = 0;
  private starting = false;

  constructor(private world: World) {
    super();
    this.camera = new THREE.PerspectiveCamera(45, 1, 0.1, 100);
  }

  async enter(): Promise<void> {
    this.scene.background = new THREE.Color(0x0b0b18);
    this.scene.fog = new THREE.Fog(0x0b0b18, 6, 16);

    const plinth = new THREE.Mesh(
      new THREE.CylinderGeometry(2.2, 2.4, 0.4, 24),
      new THREE.MeshLambertMaterial({ color: 0x3a3550 }),
    );
    plinth.position.y = -0.2;
    plinth.receiveShadow = true;
    this.scene.add(plinth);

    this.scene.add(new THREE.HemisphereLight(0x8899ff, 0x101018, 1.0));
    const key = new THREE.DirectionalLight(0xffd9a0, 1.8);
    key.position.set(3, 6, 4);
    key.castShadow = true;
    this.scene.add(key);

    this.hero = new CharacterModel(this.world.party.leader.appearance);
    this.hero.object3D.position.set(-0.6, 0, 0);
    this.hero.object3D.scale.setScalar(1.4);
    this.scene.add(this.hero.object3D);

    this.slime = new MonsterModel({ kind: "slime", color: 0x49b3ff, accent: 0x1b3a6b, scale: 1.1 });
    this.slime.object3D.position.set(1.0, 0, 0.3);
    this.scene.add(this.slime.object3D);

    this.overlay = document.createElement("div");
    this.overlay.className = "ds-center";
    this.overlay.innerHTML =
      `<div class="ds-logo">DRAGONSTACK</div>` +
      `<div class="ds-sub">a small 3D RPG, and the engine underneath it</div>` +
      `<div class="ds-press">Press Z or Enter to begin</div>`;
    this.game.ui.root.appendChild(this.overlay);
    this.game.ui.setHint("WASD move · mouse look · Z confirm · X cancel · C menu · M mute");

    await this.game.ui.fadeIn(500);
  }

  exit(): void {
    this.overlay.remove();
    this.hero.dispose();
    this.slime.dispose();
    this.disposeGraph();
    this.scene.clear();
  }

  update(dt: number): void {
    this.angle += dt * 0.35;
    const radius = 5.2;
    this.camera.position.set(Math.sin(this.angle) * radius, 2.6, Math.cos(this.angle) * radius);
    this.camera.lookAt(0, 0.9, 0);

    this.hero.update(dt);
    this.slime.update(dt);
    this.game.audio.update(dt);

    if (!this.starting && this.game.input.consume("confirm")) {
      this.starting = true;
      void this.start();
    }
  }

  private async start(): Promise<void> {
    // The first keypress is also what lets us make any sound at all.
    this.game.audio.unlock();
    this.game.audio.play("confirm");
    await this.game.ui.fadeOut(400);
    await this.game.replace(new FieldScene(this.world));
  }
}
